/*
  Scriptum engraving text field.

  The engraving product prices per character, one variant per character count. The variant
  buttons are hidden (see snippets/custom-properties-engraving.liquid) and this script keeps
  the right variant selected as the customer types, by checking the matching hidden radio
  input and firing a change event so the theme's own variant handling updates the price,
  the hidden id field and the URL.

  It also enforces the engraving restrictions on the text box: capitals only, a fixed set of
  allowed characters, and a maximum number of characters. Spaces are not engraved, so they
  do not count towards the total.

  UPGRADE NOTE: This is a custom Scriptum file.
*/
(function () {
  'use strict';

  // Everything the engraver can cut: A-Z, 0-9, & . , - : ' and spaces.
  var DISALLOWED = /[^A-Z0-9&.,\-:' ]/g;

  function readConfig() {
    var el = document.querySelector('[data-engraving-config]');
    if (!el) return null;

    try {
      var config = JSON.parse(el.textContent);
      config.steps = config.steps || [];

      // Lowest character count first, so the first step that fits is the cheapest one.
      config.steps.sort(function (a, b) {
        return a.max - b.max;
      });

      return config;
    } catch (e) {
      return null;
    }
  }

  // Uppercase and drop anything we can't engrave. Smart quotes, dashes and accented
  // letters are folded down to their plain equivalents rather than thrown away, so text
  // pasted in from a word processor survives.
  function clean(value) {
    return value
      .replace(/[\u2018\u2019\u201B\u2032]/g, "'")
      .replace(/[\u2010-\u2015\u2212]/g, '-')
      .normalize('NFD')
      .replace(/[\u0300-\u036F]/g, '')
      .toUpperCase()
      .replace(DISALLOWED, '');
  }

  // Truncate at the maximum number of engraved (non-space) characters.
  function limit(value, max) {
    var out = '';
    var count = 0;

    for (var i = 0; i < value.length; i++) {
      var character = value.charAt(i);

      if (character !== ' ') {
        if (count >= max) break;
        count++;
      } else if (count >= max) {
        // Nothing can follow the last character, so don't let trailing spaces pile up
        break;
      }

      out += character;
    }

    return out;
  }

  function countCharacters(value) {
    return value.split(' ').join('').length;
  }

  function stepFor(steps, count) {
    var fallback = null;

    for (var i = 0; i < steps.length; i++) {
      if (steps[i].max >= count) {
        if (steps[i].available) return steps[i];
        if (!fallback) fallback = steps[i];
      }
    }

    return fallback || steps[steps.length - 1];
  }

  function formatMoney(cents) {
    try {
      return window.theme.Currency.formatMoney(cents, window.theme.settings.moneyFormat);
    } catch (e) {
      return '';
    }
  }

  function Engraving(input, config) {
    this.input = input;
    this.config = config;
    this.section = input.closest('.product-section') || document;
    this.count = this.section.querySelector('[data-engraving-count]');
    this.price = this.section.querySelector('[data-engraving-price]');
    this.limitNote = this.section.querySelector('[data-engraving-limit]');
    this.radios = this.section.querySelectorAll(
      '[data-variant-input][data-index="option' + config.optionIndex + '"]'
    );

    this.onInput = this.onInput.bind(this);
    this.input.addEventListener('input', this.onInput);
    // Covers values the browser restores or autofills without firing 'input'
    this.input.addEventListener('change', this.onInput);
    this.onInput();
  }

  Engraving.prototype.sanitize = function (value) {
    var cleaned = clean(value);
    return this.config.max > 0 ? limit(cleaned, this.config.max) : cleaned;
  };

  Engraving.prototype.onInput = function () {
    var value = this.input.value;
    var next = this.sanitize(value);

    if (next !== value) {
      // Keep the caret where the customer left it, allowing for anything we removed.
      var caret = null;
      if (document.activeElement === this.input && this.input.selectionStart !== null) {
        caret = this.sanitize(value.slice(0, this.input.selectionStart)).length;
      }

      this.input.value = next;

      if (caret !== null) {
        caret = Math.min(caret, next.length);
        this.input.setSelectionRange(caret, caret);
      }
    }

    this.update(countCharacters(next));
  };

  Engraving.prototype.update = function (count) {
    // No character-count option on this product: nothing to count, price or select
    if (this.config.max < 1) return;

    var step = this.config.steps.length ? stepFor(this.config.steps, count) : null;
    var atLimit = count >= this.config.max;

    if (this.count) {
      this.count.textContent = count + ' / ' + this.config.max + ' characters';
      this.count.classList.toggle('is-at-limit', atLimit);
    }

    if (this.price && step) {
      this.price.innerHTML = formatMoney(step.price);
    }

    if (this.limitNote) {
      this.limitNote.hidden = !atLimit;
    }

    if (step) {
      this.select(step);
    }
  };

  // Check the hidden variant radio for this character count and let the theme do the rest.
  Engraving.prototype.select = function (step) {
    for (var i = 0; i < this.radios.length; i++) {
      var radio = this.radios[i];

      if (radio.value === step.value) {
        if (!radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }
    }
  };

  document.addEventListener('DOMContentLoaded', function () {
    var config = readConfig();
    var input = document.querySelector('[data-engraving-input]');

    if (config && input) {
      new Engraving(input, config);
    }
  });
})();
